import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, MultiSelectField, Toggle, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { KleinschmidtEventsOptions, getKleinschmidtEventsOptions } from '../../services/kleinschmidt.service';

/**
 * Bespoke edit/create drawer for Kleinschmidt Event Sets (cms.kleinschmidt_events).
 *
 * Mirrors V1 editkleinschmidt_events: an EDI Handle + CMS Code select, the Bill To /
 * Customer / Broker / SCAC targets (multiselects over comma-delimited recno/SCAC list
 * columns — a chip list under each keeps every selection visible), a Stagger Events
 * toggle, and the ~30 lifecycle event toggles that decide which EDI events fire. Save
 * rules (V1 cantbeempty): EDI Handle required + at least one Bill To / Customer / SCAC /
 * CMS Code (broker doesn't count).
 */
const TABLE = 'kleinschmidt-events';

const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');
const splitList = (v: any): string[] => s(v).split(',').map((x) => x.trim()).filter(Boolean);

const LIST_COLS = ['billtolist', 'customerlist', 'brokerlist', 'scaclist'];

const EVENT_GROUPS: { title: string; items: [string, string][] }[] = [
  { title: 'Order & Appointments', items: [
    ['orderreceived', 'Order Received'], ['pickupapp', 'Pickup Appointment'],
    ['deliverbydate', 'Delivery Appointment'], ['emptypickupapp', 'Empty Pickup Appointment'],
    ['terminateappt', 'Terminate Appointment'],
  ] },
  { title: 'Rail & Availability', items: [
    ['traindepart', 'Train Departed'], ['arrivalgrounded', 'Arrival/Grounded'],
    ['availgtg', 'Available/Good to Go'], ['readyforscheduling', 'Ready for Scheduling'],
  ] },
  { title: 'Pickup & Outgate', items: [
    ['arrivedpickup', 'Arrived at Pickup Location'], ['departedpickup', 'Departed Pickup Location'],
    ['outgate', 'Outgated'],
  ] },
  { title: 'Delivery', items: [
    ['deliverydate', 'Arrived at Delivery Location'], ['departdeliv', 'Departed Delivery Location'],
    ['dropdepart', 'Departed After Dropping Container'], ['droprelease', 'Drop Released'],
  ] },
  { title: 'Empty Return', items: [
    ['deliveryplusonedate', 'Arrived Empty Pickup'], ['emptypickup', 'Empty Pickup'],
    ['emptyreturn', 'Empty Return'], ['departedemptyreturn', 'Departed Empty Return'],
  ] },
  { title: 'Dates & Reference', items: [
    ['lastfreeday', 'Last Free Day'], ['perdiemlfd', 'Per Diem LFD'],
    ['container', 'Equipment Number'], ['sealnumber', 'Seal Number'],
    ['etadate', 'ETA Date'], ['revisedeta', 'Revised ETA'],
  ] },
  { title: 'Warehouse & Documents', items: [
    ['rcvarrived', 'RCV Arrived'], ['blshipped', 'BL Shipped'],
    ['whsecheckin', 'Whse Check In'], ['sendpoddoc', 'Send POD Document'],
  ] },
];
const EVENT_KEYS = EVENT_GROUPS.flatMap((g) => g.items.map(([k]) => k));
const YN_KEYS = ['staggerevents', ...EVENT_KEYS];

const KleinschmidtEventsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<KleinschmidtEventsOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getKleinschmidtEventsOptions().then(setOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  // Seed the form from the record.
  useEffect(() => {
    setForm({
      edihandle: s(record.edihandle),
      cmscode: s(record.cmscode),
      billtolist: splitList(record.billtolist),
      customerlist: splitList(record.customerlist),
      brokerlist: splitList(record.brokerlist),
      scaclist: splitList(record.scaclist),
      ...Object.fromEntries(YN_KEYS.map((k) => [k, yn(record[k])])),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const setYN = (k: string, checked: boolean) => setField(k, checked ? 'Y' : 'N');

  // Option lists (recno-valued, label carries the recno for cross-checking against the
  // stored comma lists).
  const edihandleOpts = useMemo(() => normalizeOptions(opts?.edihandles ?? []), [opts]);
  const cmscodeOpts = useMemo(() => normalizeOptions(opts?.cmscodes ?? []), [opts]);
  const billtoOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({
    value: String(b.recno), label: `${titleCase(s(b.cname))} · #${b.recno}`,
  })), [opts]);
  const customerOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({
    value: String(c.recno), label: `${titleCase(s(c.custname))} · #${c.recno}`,
  })), [opts]);
  const brokerOpts = useMemo(() => (opts?.brokers ?? []).map((b) => ({
    value: String(b.recno), label: `${titleCase(s(b.name))} · #${b.recno}`,
  })), [opts]);
  const scacOpts = useMemo(() => opts?.scacs ?? [], [opts]);

  const arr = (k: string): string[] => (Array.isArray(form[k]) ? (form[k] as string[]) : []);

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.edihandle).trim()) errs.push('EDI Handle is required.');
    const hasTarget = arr('billtolist').length > 0 || arr('customerlist').length > 0
      || arr('scaclist').length > 0 || !!s(form.cmscode).trim();
    if (!hasTarget) errs.push('Must have at least one Bill To, Customer, SCAC, or CMS Code specified.');
    return errs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const editable: TablemaintRow = {
      edihandle: s(form.edihandle).trim(),
      cmscode: s(form.cmscode),
      billtolist: arr('billtolist').join(','),
      customerlist: arr('customerlist').join(','),
      brokerlist: arr('brokerlist').join(','),
      scaclist: arr('scaclist').join(','),
      ...Object.fromEntries(YN_KEYS.map((k) => [k, yn(form[k])])),
    };
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, editable);
      } else {
        const patch: TablemaintRow = {};
        Object.entries(editable).forEach(([k, v]) => {
          const cur = YN_KEYS.includes(k) ? yn(record[k]) : s(record[k]);
          if (v !== cur) patch[k] = v;
        });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save Kleinschmidt event set.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Event Set' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  // Identified by EDI handle rather than recno, same as the partners drawer;
  // recno is the fallback when the handle is blank.
  const ident = s(record.edihandle).trim() || `#${recno}`;
  const title = isNew ? 'Add Kleinschmidt Event Set' : `Edit Kleinschmidt Event Set (${ident})`;

  // A multiselect target + a visible chip list of the current selections (labels resolved
  // from the options; orphaned values — e.g. a since-deleted record — show as #recno so
  // nothing silently disappears).
  const renderTarget = (key: string, label: string, options: { value: string; label: string }[], placeholder: string) => {
    const values = arr(key);
    const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? `#${v}`;
    return (
      <div>
        <MultiSelectField label={label} value={values} options={options} disabled={isSaving}
          placeholder={placeholder} onChange={(v) => setField(key, v)} />
        {values.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                {labelFor(v)}
                {!isSaving && (
                  <button type="button" onClick={() => setField(key, values.filter((x) => x !== v))}
                    className="hover:text-blue-900 dark:hover:text-blue-100">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="64rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Config */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Configuration</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="EDI Handle" required value={s(form.edihandle)} disabled={isSaving}
              onChange={(v) => setField('edihandle', v)} options={edihandleOpts} />
            <SelectField label="CMS Code" value={s(form.cmscode)} disabled={isSaving}
              onChange={(v) => setField('cmscode', v)} options={cmscodeOpts} />
          </div>
          <div className="mt-3">
            <Toggle label="Stagger Events?" checked={s(form.staggerevents) === 'Y'} disabled={isSaving}
              onChange={(v) => setYN('staggerevents', v)} />
          </div>
        </fieldset>

        {/* Targets */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <legend className="px-2 text-sm font-semibold text-label">
            Applies To <span className="text-xs font-normal text-gray-400">(at least one Bill To, Customer, SCAC, or CMS Code)</span>
          </legend>
          {renderTarget('billtolist', 'Bill To(s)', billtoOpts, 'Add bill tos…')}
          {renderTarget('customerlist', 'Customer(s)', customerOpts, 'Add customers…')}
          {renderTarget('brokerlist', 'Broker(s)', brokerOpts, 'Add brokers…')}
          {renderTarget('scaclist', 'SCAC(s)', scacOpts, 'Add SCACs…')}
        </fieldset>

        {/* Events */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <legend className="px-2 text-sm font-semibold text-label">Events to Trigger</legend>
          {EVENT_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">{g.title}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {g.items.map(([k, label]) => (
                  <Toggle key={k} label={label} checked={s(form[k]) === 'Y'} disabled={isSaving}
                    onChange={(v) => setYN(k, v)} />
                ))}
              </div>
            </div>
          ))}
        </fieldset>

        {/* Soft-delete (edit only). */}
        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
              hint="Soft-deletes this event set — hidden from filters but preserved in history. Uncheck and save to restore." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default KleinschmidtEventsEditDrawer;
